import React from 'react';

import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import isNull from 'lodash/isNull';
import {
    MENU_TAB_CLASS_BLOCK_NAME,
    MENU_TAB_COUNTER_CLASS_NAME,
    MENU_TAB_COUNTER_HIDDEN_MODIFIER,
    MENU_TAB_SELECTED_MODIFIER,
    MENU_TAB_FLUID_MODIFIER,
    MENU_TAB_LABEL_CLASS_NAME,
 } from './constants';
import { create } from '../../utils/dom';
import Icon from '../icon';
import { SIZE_28x28 } from '../icon/constants';
import { rewriteUrl } from '~shared/utils/uriAliases';
import './styles/tab.scss';
import './styles/label.scss';
import '../../styles/bb-counters/bb-counter-events.scss';

/**
 * Creates a menu tab
 * @param  {String} [labelText] Optional label for the tab
 * @param  {Icon} [iconName] Optional svg icon
 * @param  {String|Function} [onClick] Url to link to or onclick function callback
 * @param  {Boolean} isFavouriteSport true if is favourite sport
 * @return {MenuTab} The top menu object
 */
export default function createMenuTab(labelText, iconName, onClick, isFavouriteSport = false) {
    /**
     * The MenuTab dom element
     * @type {Element}
     */
    let el;

    if (typeof onClick === 'string') {
        el = create('a', MENU_TAB_CLASS_BLOCK_NAME);
    } else {
        el = create('span', MENU_TAB_CLASS_BLOCK_NAME);
    }

    /**
     * The tab icon
     * @type {Icon}
     */
    let icon = null;

    if (!isEmpty(iconName)) {
        icon = new Icon(iconName, SIZE_28x28);
        el.appendChild(icon.el);
    }

    if (!isEmpty(labelText)) {
        const label = create('div', MENU_TAB_LABEL_CLASS_NAME, labelText);
        el.appendChild(label);

        addModifier(MENU_TAB_FLUID_MODIFIER);
    }

    let favouriteStar = null;

    /**
     * Sets whether the favouerite sport star is shown on the tab.
     * @param {Boolean} shouldShow shows star if true.
     * @return {void}
     */
    function setFavouriteSportStar(shouldShow = false) {
        const isShown = !isNull(favouriteStar);
        if (shouldShow && !isShown) {
            favouriteStar = create('div', 'bb-menu-tabs__tab__favourite-star');
            el.appendChild(favouriteStar);
        } else if (isShown && !shouldShow) {
            el.removeChild(favouriteStar);
            favouriteStar = null;
        }
    }

    setFavouriteSportStar(isFavouriteSport);

    /**
     * The counteru dom element
     * @type {Element}
     */
    const counter = create('div', MENU_TAB_COUNTER_CLASS_NAME);
    el.appendChild(counter);

    hideCounter();

    if (isFunction(onClick)) {
        el.addEventListener('click', onClick);
    } else {
        el.setAttribute('href', rewriteUrl(onClick));
    }

    /**
     * Adds a css bem modifier
     * @param {String} modifier The modifier name
     * @return {void}
     */
    function addModifier(modifier) {
        el.classList.add(`${MENU_TAB_CLASS_BLOCK_NAME}--${modifier}`);
    }

    /**
     * Removes a css bem modifier
     * @param {String} modifier The modifier name
     * @return {void}
     */
    function removeModifier(modifier) {
        el.classList.remove(`${MENU_TAB_CLASS_BLOCK_NAME}--${modifier}`);
    }

    /**
     * Show counter
     * @return {void}
     */
    function showCounter() {
        counter.classList.remove(MENU_TAB_COUNTER_HIDDEN_MODIFIER);
    }

    /**
     * Hide counter
     * @return {void}
     */
    function hideCounter() {
        counter.classList.add(MENU_TAB_COUNTER_HIDDEN_MODIFIER);
    }

    /**
     * Sets a count on the icon
     * @param {Number} count The count number
     * @return {void}
     */
    function setCount(count) {
        counter.textContent = count;
    }

    /**
     * Checks if the tab is selected
     * @return {Boolean} if the tab is selected or not
     */
    function isSelected() {
        return el.classList.contains(`${MENU_TAB_CLASS_BLOCK_NAME}--${MENU_TAB_SELECTED_MODIFIER}`);
    }

    /**
     * Shows the tab as slected
     * @return {void}
     */
    function showAsSelected() {
        addModifier(MENU_TAB_SELECTED_MODIFIER);
    }

    /**
     * Hides the tab as selected
     * @return {void}
     */
    function hideAsSelected() {
        removeModifier(MENU_TAB_SELECTED_MODIFIER);
    }

    return {
        el,
        icon,
        addModifier,
        removeModifier,
        showCounter,
        hideCounter,
        setCount,
        isSelected,
        showAsSelected,
        hideAsSelected,
        setFavouriteSportStar,
    };
}

/**
 * @todo: to refactor to accomplish a full react workflow component
 *
 * Creates a menu tab
 * @param  {Component} [labelContent] Optional react info component for the tab
 * @param  {Icon} [iconName] Optional svg icon
 * @param  {Function} [onClick] Optional onClick handler
 * @param  {String} [iconLabel] Optional icon text
 * @param  {String} [iconSize] Optional icon size
 * @return {MenuTab} The top menu object
 *
 */
export function createMenuTabReact(labelContent, iconName, onClick, iconLabel, iconSize = SIZE_28x28) {

    let classes = '';
    /**
     * The tab icon
     * @type {Icon}
     */
    let icon = null;
    let iconHTML = null;
    if (!isEmpty(iconName)) {
        /*
         * @todo: pay attention! Icon is legacy code which is returning a dom
         * element instead of a React one. That's why we have to access the
         * dom with a ref and attach this icon. To be refactored, so as to
         * have a common React component instead.
         */
        icon = new Icon(iconName, iconSize, iconLabel);
        iconHTML = icon.el;
    }

    let label = null;
    if (!isEmpty(labelContent)) {
        label =
            <div
                className={MENU_TAB_LABEL_CLASS_NAME}
            >
                {labelContent}
            </div>;
        classes += `${MENU_TAB_CLASS_BLOCK_NAME}--${MENU_TAB_FLUID_MODIFIER}`;
    }

    /**
     * The counter dom element
     * @type {Element}
     */
    let counterRef = null; // Won't be available until rendered
    const counter = <div ref={_counterRef => ( counterRef = _counterRef )} className={`${MENU_TAB_COUNTER_CLASS_NAME} ${MENU_TAB_COUNTER_HIDDEN_MODIFIER}`} />;

    let _onClick = null;
    if (isFunction(onClick)) {
        _onClick = onClick;
    }

    /**
     * The MenuTab dom element
     * @type {Element}
     */
    let elRef = null; // Won't be available until rendered
    /*
     * @todo: refactor Icon code and affected stuff, so we have a react component
     * instead of plain HTML, that forces us to access the dom (ref) to append it
     */
    const el =
        <span
            ref={_elRef => {
                elRef = _elRef;
                if (elRef && iconHTML) {
                    elRef.insertBefore(iconHTML, elRef.firstChild);
                }
            }}
            className={`${MENU_TAB_CLASS_BLOCK_NAME} ${classes} ${MENU_TAB_CLASS_BLOCK_NAME}--${iconName}`}
            onClick={_onClick}
        >
            {label}
            {counter}
        </span>;

    /**
     * Adds a css bem modifier
     * @param {String} modifier The modifier name
     * @return {void}
     */
    function addModifier(modifier) {
        elRef.classList.add(`${MENU_TAB_CLASS_BLOCK_NAME}--${modifier}`);
    }

    /**
     * Removes a css bem modifier
     * @param {String} modifier The modifier name
     * @return {void}
     */
    function removeModifier(modifier) {
        elRef.classList.remove(`${MENU_TAB_CLASS_BLOCK_NAME}--${modifier}`);
    }

    /**
     * Show counter
     * @return {void}
     */
    function showCounter() {
        counterRef.classList.remove(MENU_TAB_COUNTER_HIDDEN_MODIFIER);
    }

    /**
     * Hide counter
     * @return {void}
     */
    function hideCounter() {
        counterRef.classList.add(MENU_TAB_COUNTER_HIDDEN_MODIFIER);
    }

    /**
     * Sets a count on the icon
     * @param {Number} count The count number
     * @return {void}
     */
    function setCount(count) {
        counterRef.textContent = count;
    }

    /**
     * Checks if the tab is selected
     * @return {Boolean} if the tab is selected or not
     */
    function isSelected() {
        return elRef.classList.contains(`${MENU_TAB_CLASS_BLOCK_NAME}--${MENU_TAB_SELECTED_MODIFIER}`);
    }

    /**
     * Shows the tab as slected
     * @return {void}
     */
    function showAsSelected() {
        addModifier(MENU_TAB_SELECTED_MODIFIER);
    }

    /**
     * Hides the tab as selected
     * @return {void}
     */
    function hideAsSelected() {
        removeModifier(MENU_TAB_SELECTED_MODIFIER);
    }

    return {
        el,
        icon,
        addModifier,
        removeModifier,
        showCounter,
        hideCounter,
        setCount,
        isSelected,
        showAsSelected,
        hideAsSelected,
    };
}
